<template>
    <div class="app-tip-summary-table">
        <TipSummaryTable
            :totalConsumption="currentTotalConsumption"
            :discountAmount="currentDiscountAmount"
            :tip="currentTip"
            :fees="currentFees"
            :showFees="cart.isDeliveryActive"
            :hasDiscounts="hasDiscounts"
            :showTip="currentShowTip"
            :isDiscountClickable="currentIsDiscountClickable"
            :total="currentTotal"
            :onDiscountClick="onDiscountClick"
        />
    </div>
</template>

<script setup lang="ts">
    import { BillDiscountsListPopover } from "@/app/popover/BillDiscountsListPopover";
    import { CartDiscountsListPopover } from "@/app/popover/CartDiscountsListPopover";
    import { useKioskStore } from "@/app/stores/kiosk";
    import { useCartStore } from "@/data/stores/cart";
    import { type Ref, computed } from "vue";

    import TipSummaryTable from "@/components/pay/TipSummaryTable.vue";

    // define props
    const props = defineProps<{
        totalConsumption: Ref<number>;
        fees: Ref<number>;
        discountAmount: Ref<number>;
        hasDiscounts: Ref<boolean>;
        tip: Ref<number | null>;
        showTip: Ref<boolean>;
        isDiscountClickable?: Ref<boolean>;
        isPayService: Ref<boolean>;
        total?: Ref<number>;
        onDiscountRemoved: () => void;
    }>();

    const cart = useCartStore();
    const kiosk = useKioskStore();

    const currentTotalConsumption = computed(() => props.totalConsumption.value);
    const currentFees = computed(() => props.fees.value);
    const currentDiscountAmount = computed(() => props.discountAmount.value);
    const hasDiscounts = computed(() => props.hasDiscounts.value);
    const currentTip = computed(() => props.tip.value);
    const currentShowTip = computed(() => props.showTip.value);
    const currentIsDiscountClickable = computed(() => props.isDiscountClickable?.value);
    // the total row belongs to the kiosk only: the app and the operator keep the table
    // as it was. undefined here means TipSummaryTable renders no row at all
    const currentTotal = computed(() => (kiosk.isKioskMode ? props.total?.value : undefined));

    function onDiscountClick() {
        if (props.isPayService.value) {
            new BillDiscountsListPopover(props.onDiscountRemoved).show();
        } else {
            new CartDiscountsListPopover(props.onDiscountRemoved).show();
        }
    }
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_variables.scss";

    .app-tip-summary-table {
        padding: 0 1.5em;
        background-color: #fff;

        .tip-summary-table {
            padding: 0.625em 0;
            border-top-color: #f5f5f5;

            .total {
                line-height: 1.35em;
            }
        }
    }

    // kiosk: both levels size themselves in em, so the font-size scales their paddings
    // and line-heights with it. the wrapper needs its own, for its 1.5em padding, and
    // the inner table cannot just inherit it because TipSummaryTable pins it to 16px.
    // it sits directly on the bottom band, so it carries the band's doubling too - and
    // because everything below is em, these two anchors are the whole change
    .kiosk-mode .app-tip-summary-table {
        padding: 0;
        font-size: 20px * variables.$kioskScale;

        .tip-summary-table {
            font-size: 20px * variables.$kioskScale;


            .total {
                line-height: 1.6em;

                // the amount actually charged, so it reads bigger than the rows above it
                &.total-to-pay {
                    font-size: 1.5em;
                    // re-stated so it resolves against the bigger font-size instead of
                    // inheriting the computed value of the rows above
                    line-height: 1.6em;
                    font-weight: 600;

                    // the currency cell's 1em padding grows with the font-size above,
                    // so divide the font-size back out to keep the gap between the
                    // currency and the amount at the same px as the rows above
                    td.currency {
                        padding: 0 calc(1em / 1.5);
                    }
                }
            }
        }
    }
</style>
